import type { MessageDescriptor } from 'react-intl';

import { ACTION, EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { CustomData } from '@atlaskit/ufo/types';
import type { DuplicateMauiAppSuccess } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';
import {
	ExperienceName,
	startRemixPasteRenderEmbedExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { getActiveRemixCreateId } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { ExperienceTrackerHandler } from '@atlassian/conversation-assistant-instrumentation/main';

import { messages } from '../ui/messages';
import { canCreatePageInSpace } from '../utils/can-create-page-in-space';
import {
	buildDuplicatedMauiAppUrl,
	buildMovedUrl,
	extractPastedUrl,
	parseMauiAppLink,
	parseMauiDiagramLink,
	type RovoConversationContext,
	WHITEBOARD_PATH_REGEX,
} from './detect-maui-link';
import {
	prepareMauiAppPaste,
	getPastedMauiApps,
	docContainsMauiAppVersion,
	updatePastedMauiApp,
	sliceContainsMauiApp,
	type PastedMauiApp,
} from './maui-app-paste';

const DUPLICATE_FALLBACK_ERROR_MESSAGE = 'Failed to duplicate MAUI app.';
const REMIX_PASTE_SOURCE = 'mauiDiagramPastePlugin';

const getMauiExperienceTracker = () => ExperienceTrackerHandler.getInstance().experienceTracker;

const toRemixPasteRenderEmbedMetadata = (attributes: Record<string, unknown>): CustomData =>
	Object.fromEntries(
		Object.entries(attributes).filter(
			(entry): entry is [string, string | number | boolean | undefined] => {
				const value = entry[1];
				return (
					value === undefined ||
					typeof value === 'string' ||
					typeof value === 'number' ||
					typeof value === 'boolean'
				);
			},
		),
	);

const startRemixPasteExperience = (attributes: Record<string, unknown>) => {
	const experienceTracker = getMauiExperienceTracker();
	const featureArea = getRemixFeatureArea({
		remixType: attributes.remixType as string | undefined,
	});
	const enrichedAttributes = {
		...attributes,
		...(featureArea !== undefined && { featureArea }),
	};
	experienceTracker.start({
		name: ExperienceName.REMIX_PASTE,
		id: ExperienceName.REMIX_PASTE,
		attributes: enrichedAttributes,
	});
	startRemixPasteRenderEmbedExperience(toRemixPasteRenderEmbedMetadata(enrichedAttributes));
};

const succeedRemixPasteExperience = (attributes: Record<string, unknown>) => {
	const experienceTracker = getMauiExperienceTracker();
	const featureArea = getRemixFeatureArea({
		remixType: attributes.remixType as string | undefined,
	});
	experienceTracker.succeed({
		name: ExperienceName.REMIX_PASTE,
		attributes: {
			...attributes,
			...(featureArea !== undefined && { featureArea }),
		},
	});
};

const failRemixPasteExperience = (error: Error, attributes: Record<string, unknown>) => {
	const experienceTracker = getMauiExperienceTracker();
	const featureArea = getRemixFeatureArea({
		remixType: attributes.remixType as string | undefined,
	});
	experienceTracker.fail({
		teamName: 'CC CE MAU',
		name: ExperienceName.REMIX_PASTE,
		error,
		attributes: {
			...attributes,
			...(featureArea !== undefined && { featureArea }),
		},
	});
};

const failRemixDuplicateAppAndInsert = (error: Error, attributes?: Record<string, unknown>) => {
	const experienceTracker = getMauiExperienceTracker();
	const baseAttributes = attributes ?? {};
	const featureArea = getRemixFeatureArea({
		remixType: attributes?.remixType as string | undefined,
	});
	const enrichedAttributes = {
		...baseAttributes,
		...(featureArea !== undefined && { featureArea }),
	};
	experienceTracker.fail({
		teamName: 'CC CE MAU',
		name: ExperienceName.REMIX_DUPLICATE_APP,
		error,
		attributes: { ...enrichedAttributes, remixCreateId: getActiveRemixCreateId() },
	});
	experienceTracker.fail({
		teamName: 'CC CE MAU',
		name: ExperienceName.REMIX_INSERT,
		error,
		attributes: {
			...enrichedAttributes,
			failedDueTo: ExperienceName.REMIX_DUPLICATE_APP,
			remixCreateId: getActiveRemixCreateId(),
		},
	});
};

export type MauiDiagramLinkInfo = {
	contentId: string;
	/** Conversation context extracted from _r* query params, if present. */
	conversationContext?: RovoConversationContext;
	originalUrl: string;
};

export type MauiAppLinkInfo = {
	appId: string;
	originalUrl: string;
};

export type MauiCurrentSpace = {
	canCreatePage?: boolean;
	iconPath?: string;
	id: string;
	key?: string;
	name: string;
	type?: string;
};

export type MauiFinalizeLocationConfig = {
	currentParentContent?: {
		contentType: string;
		id: string;
		subType?: string;
		title: string;
	};
	/**
	 * Optional fallback snapshot for the picker's `currentSpace` slot, used
	 * when the platform `getCurrentSpaceSnapshot(siteId, targetSpaceKey)`
	 * cache has not been populated yet (e.g. the picker mounts before the
	 * pre-flight `canCreatePageInSpace` probe has resolved).
	 */
	currentSpace?: MauiCurrentSpace;
	siteId: string;
	userId?: string;
};

export type MauiEmbedTargetSpace = {
	id: string;
	key: string;
	parentId: string;
};

export type MauiDiagramPasteConfig = {
	/**
	 * Duplicates a MAUI app under the current page so it inherits page permissions.
	 * Resolves with duplicate telemetry fields for analytics and experiences.
	 */
	duplicateMauiApp?: (
		appId: string,
		containerContentId: string,
		contentType?: string,
	) => Promise<DuplicateMauiAppSuccess>;
	/** Move/finalize a pasted MAUI whiteboard. Rejects surface as plugin flags. Only required when the move workflow is supported (e.g. Confluence). */
	finalizeAfterMove?: (
		contentId: string,
		parentPageId: string,
		conversationContext?: RovoConversationContext,
		targetSpace?: MauiEmbedTargetSpace,
	) => Promise<void>;
	finalizeLocation?: MauiFinalizeLocationConfig;
	fireAnalyticsEvent?: (payload: {
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Record<string, unknown>;
		eventType: string;
	}) => void;
	/**
	 * Returns the content type of the current page/blog ('page' or 'blogpost').
	 * Used to construct the correct ARI when duplicating a MAUI app.
	 */
	getCurrentContentType?: () => string | undefined;
	getCurrentPageId?: () => string | undefined;
	/**
	 * Optional predicate that classifies a `finalizeAfterMove` rejection as a
	 * user-permission error for the paste workflow's flag copy. The Slice 2
	 * in-embed indicator workflow does NOT use this — it relies on the
	 * positive pre-flight `canCreatePageInSpace` probe instead (called
	 * directly via `@atlassian/conversation-assistant-action-helpers`).
	 */
	isFinalizePermissionError?: (error: Error) => boolean;
	/** Resolves the space key for a given content ID. Only required when the move workflow is supported (e.g. Confluence). */
	resolveContentSpaceKey?: (contentId: string) => Promise<string | null>;
	/** The target space key for moved diagrams. Only required when the move workflow is supported (e.g. Confluence). */
	targetSpaceKey?: string;
};

export enum MauiDiagramPasteFlagType {
	ERROR = 'error',
	INFO = 'info',
	SUCCESS = 'success',
}

export type MauiDiagramPasteFlag = {
	description?: string;
	id: string;
	title?: string;
	type: MauiDiagramPasteFlagType;
};

/**
 * Tracks the per-content-id finalize state of MAUI whiteboard embeds that
 * users encounter while editing a page. The same content id may appear in
 * the document multiple times (or be unmounted/remounted as the user
 * scrolls), so the result is cached on the plugin so that the indicator can
 * read it without redundant network calls.
 */
export enum MauiEmbedFinalizeStatus {
	/** No information yet — needs to call `resolveContentSpaceKey`. */
	UNKNOWN = 'UNKNOWN',
	/** This client owns the in-progress Remix stream for the whiteboard. */
	STREAMING = 'STREAMING',
	/** Resolution is in flight. */
	CHECKING = 'CHECKING',
	/** Whiteboard is parented in a Rovo system space and needs finalize. */
	NEEDS_FINALIZE = 'NEEDS_FINALIZE',
	/** Already in a real (non-Rovo) space — nothing to do. */
	NOT_NEEDED = 'NOT_NEEDED',
	/** Finalize is in flight. */
	FINALIZING = 'FINALIZING',
	/** Finalize succeeded; URL has been rewritten. */
	FINALIZED = 'FINALIZED',
	/** Resolve or finalize failed — user can retry. */
	FAILED = 'FAILED',
	/** `canCreatePageInSpace` returned `false`; the picker mounts. */
	NEEDS_SPACE_SELECTION = 'NEEDS_SPACE_SELECTION',
}

export type MauiEmbedFinalizeStateEntry = {
	errorMessage?: string;
	originalUrl?: string;
	pickerOpen?: boolean;
	status: MauiEmbedFinalizeStatus;
};

export type MauiDiagramPastePluginState = {
	activeFlag: MauiDiagramPasteFlag | null;
	/**
	 * Per-content-id finalize state for MAUI whiteboard embeds rendered on
	 * the page. Indexed by the whiteboard content id (the numeric/UUID
	 * segment from the URL).
	 */
	embedFinalizeStates: Record<string, MauiEmbedFinalizeStateEntry>;
	isMoving: boolean;
};

export enum ActionType {
	MOVE_STARTED = 'MOVE_STARTED',
	MOVE_SUCCEEDED = 'MOVE_SUCCEEDED',
	MOVE_FAILED = 'MOVE_FAILED',
	DUPLICATE_STARTED = 'DUPLICATE_STARTED',
	DUPLICATE_SUCCEEDED = 'DUPLICATE_SUCCEEDED',
	DUPLICATE_FAILED = 'DUPLICATE_FAILED',
	DISMISS_FLAG = 'DISMISS_FLAG',
	EMBED_FINALIZE_STATUS_CHANGED = 'EMBED_FINALIZE_STATUS_CHANGED',
	/** Drops the per-content-id entry so the next mount re-runs the probe. */
	EMBED_FINALIZE_RESET = 'EMBED_FINALIZE_RESET',
	/** Opens the space picker for a NEEDS_SPACE_SELECTION embed. */
	EMBED_FINALIZE_OPEN_PICKER = 'EMBED_FINALIZE_OPEN_PICKER',
	/** Closes the space picker without resetting the embed's status. */
	EMBED_FINALIZE_CLOSE_PICKER = 'EMBED_FINALIZE_CLOSE_PICKER',
}

type CardMutation = {
	newAttrs: Record<string, unknown>;
	pos: number;
};

type LinkMarkMutation = {
	from: number;
	newMark: PMNode['marks'][number];
	oldMark: PMNode['marks'][number];
	to: number;
};

type ExtensionMutation = {
	newAttrs: Record<string, unknown>;
	pos: number;
};

type TextReplaceMutation = {
	from: number;
	newUrl: string;
	to: number;
};

export const mauiDiagramPastePluginKey: PluginKey<MauiDiagramPastePluginState> =
	new PluginKey<MauiDiagramPastePluginState>('mauiDiagramPastePlugin');

/**
 * Set of currently-mounted EditorViews that have the diagram-paste PM
 * plugin installed. Populated by the plugin's `view()` lifecycle on mount
 * and removed on `destroy()`. Consulted by the `executeMauiEmbedFinalize`
 * action so it can dispatch transactions spanning the async finalize
 * workflow without callers needing to thread a view through.
 *
 * Why a Set (not a WeakMap keyed by PluginKey): `mauiDiagramPastePluginKey`
 * is a module-level singleton, so all live editor instances share the
 * exact same key. A WeakMap keyed by that singleton would only ever store
 * one slot — the last editor to mount would silently overwrite earlier
 * entries, and a stale entry from any earlier editor would shadow newer
 * ones once that editor unmounted. Using a Set lets us track every live
 * editor independently and disambiguate per-call via `originalUrl`.
 */
const knownViews: Set<EditorView> = new Set();
const knownViewIntlGetters: WeakMap<EditorView, PMPluginFactoryParams['getIntl']> = new WeakMap();
const embedRemovalObservers: WeakMap<EditorView, Map<string, Set<() => void>>> = new WeakMap();

const docContainsMauiContentId = (doc: PMNode, expectedContentId: string): boolean => {
	let found = false;
	doc.descendants((node) => {
		const candidateUrls = [
			typeof node.attrs?.url === 'string' ? node.attrs.url : undefined,
			typeof node.attrs?.parameters?.url === 'string' ? node.attrs.parameters.url : undefined,
			typeof node.attrs?.parameters?.macroParams?.url?.value === 'string'
				? node.attrs.parameters.macroParams.url.value
				: undefined,
		];
		found = candidateUrls.some(
			(url) => url && WHITEBOARD_PATH_REGEX.exec(url)?.[2] === expectedContentId,
		);
		return !found;
	});
	return found;
};

/** Observes removal only for a currently mounted, exact MAUI embed. */
export const observeMauiEmbedRemoval = (
	contentId: string,
	onRemoved: () => void,
): (() => void) | undefined => {
	const view = [...knownViews].find((candidate) =>
		docContainsMauiContentId(candidate.state.doc, contentId),
	);
	if (!view) {
		return undefined;
	}
	const observersByContentId = embedRemovalObservers.get(view) ?? new Map();
	const observers = observersByContentId.get(contentId) ?? new Set();
	observers.add(onRemoved);
	observersByContentId.set(contentId, observers);
	embedRemovalObservers.set(view, observersByContentId);
	return () => {
		observers.delete(onRemoved);
		if (observers.size === 0) {
			observersByContentId.delete(contentId);
		}
	};
};

/**
 * Deferred to a microtask so observers (React state, Remix lifecycle) never run inside a ProseMirror
 * transition, and a document restored within the same tick does not fire. Re-checks the live doc.
 */
const scheduleRemovedMauiEmbedNotification = (view: EditorView): void => {
	const observersByContentId = embedRemovalObservers.get(view);
	if (!observersByContentId || observersByContentId.size === 0) {
		return;
	}
	queueMicrotask(() => {
		const liveObservers = embedRemovalObservers.get(view);
		if (!liveObservers || view.isDestroyed) {
			return;
		}
		for (const [contentId, observers] of liveObservers) {
			if (docContainsMauiContentId(view.state.doc, contentId)) {
				continue;
			}
			liveObservers.delete(contentId);
			observers.forEach((observer) => observer());
		}
	});
};

const notifyAllMauiEmbedsRemoved = (view: EditorView): void => {
	const observersByContentId = embedRemovalObservers.get(view);
	embedRemovalObservers.delete(view);
	observersByContentId?.forEach((observers) => observers.forEach((observer) => observer()));
};

/**
 * @internal — exposed only for the malleableUi plugin's action layer.
 *
 * When `originalUrl` is provided, returns the EditorView whose document
 * contains that URL — the only correct view to dispatch a finalize
 * workflow against when multiple editors are concurrently alive (e.g.
 * comment editor + page editor on the same screen).
 *
 * When `originalUrl` is omitted, or no view contains the URL, returns
 * an arbitrary mounted view (or undefined if none are mounted).
 */
export function getMauiDiagramPasteView(originalUrl?: string): EditorView | undefined {
	if (originalUrl) {
		for (const view of knownViews) {
			if (docContainsUrl(view.state.doc, originalUrl)) {
				return view;
			}
		}
	}
	// Fallback: no URL provided, or no editor currently contains the URL.
	// Return the first known view — callers without a URL hint accept that
	// in multi-editor scenarios this is best-effort.
	return knownViews.values().next().value;
}

const fallbackGetIntl: PMPluginFactoryParams['getIntl'] = () =>
	({
		formatMessage: ({ defaultMessage }: { defaultMessage: string }) => defaultMessage,
	}) as ReturnType<PMPluginFactoryParams['getIntl']>;

const formatFlagMessage = (getIntl: PMPluginFactoryParams['getIntl'], message: MessageDescriptor) =>
	getIntl().formatMessage(message);

const initialState: MauiDiagramPastePluginState = {
	isMoving: false,
	activeFlag: null,
	embedFinalizeStates: {},
};

const CARD_NODE_NAMES = new Set(['inlineCard', 'blockCard', 'embedCard']);
const ROVO_SPACE_PREFIX = '~ROVO';

/**
 * Checks whether the document already contains a reference to the given URL.
 * Only inspects card nodes and extension nodes (both flat and macroParams
 * formats) — link marks and plain text are intentionally skipped.
 *
 * Returns `true` on the first match, stopping traversal early.
 */
function docContainsUrl(doc: PMNode, url: string): boolean {
	let found = false;

	doc.descendants((node) => {
		if (found) {
			return false;
		}

		if (CARD_NODE_NAMES.has(node.type.name) && node.attrs?.url?.includes(url)) {
			found = true;
			return false;
		}

		if (node.type.name === 'extension') {
			// Flat format: parameters.url
			if (node.attrs?.parameters?.url?.includes(url)) {
				found = true;
				return false;
			}
			// Native embed macro format: parameters.macroParams.url.value
			if (node.attrs?.parameters?.macroParams?.url?.value?.includes(url)) {
				found = true;
				return false;
			}
		}

		return true;
	});

	return found;
}

/**
 * Searches the editor document for any occurrence of the original URL and
 * replaces it with the new URL. Handles:
 * - inlineCard / blockCard / embedCard nodes with a url attribute
 * - extension nodes with a parameters.url attribute (native embeds)
 * - Hyperlink marks with the URL as href
 * - Plain text nodes containing the URL
 *
 * Mutations are collected during traversal and applied in reverse document
 * order so that earlier positions are not invalidated by later mutations.
 */
function updateUrlInDoc(tr: Transaction, originalUrl: string, newUrl: string): void {
	const cardMutations: CardMutation[] = [];
	const extensionMutations: ExtensionMutation[] = [];
	const linkMarkMutations: LinkMarkMutation[] = [];
	const textReplaceMutations: TextReplaceMutation[] = [];

	tr.doc.descendants((node, pos) => {
		if (CARD_NODE_NAMES.has(node.type.name) && node.attrs?.url?.includes(originalUrl)) {
			cardMutations.push({
				pos,
				newAttrs: {
					...node.attrs,
					url: node.attrs.url.replace(originalUrl, newUrl),
				},
			});
			return false;
		}

		// Native embed extension nodes store the URL in parameters.url
		if (node.type.name === 'extension' && node.attrs?.parameters?.url?.includes(originalUrl)) {
			extensionMutations.push({
				pos,
				newAttrs: {
					...node.attrs,
					parameters: {
						...node.attrs.parameters,
						url: node.attrs.parameters.url.replace(originalUrl, newUrl),
					},
				},
			});
			return false;
		}

		// Native embed macro extension nodes store the URL in parameters.macroParams.url.value
		if (
			node.type.name === 'extension' &&
			node.attrs?.parameters?.macroParams?.url?.value?.includes(originalUrl)
		) {
			extensionMutations.push({
				pos,
				newAttrs: {
					...node.attrs,
					parameters: {
						...node.attrs.parameters,
						macroParams: {
							...node.attrs.parameters.macroParams,
							url: {
								...node.attrs.parameters.macroParams.url,
								value: node.attrs.parameters.macroParams.url.value.replace(originalUrl, newUrl),
							},
						},
					},
				},
			});
			return false;
		}

		if (node.isText) {
			node.marks?.forEach((mark) => {
				if (mark.type.name === 'link' && mark.attrs?.href?.includes(originalUrl)) {
					const newHref = mark.attrs.href.replace(originalUrl, newUrl);
					const newMark = mark.type.create({ ...mark.attrs, href: newHref });
					linkMarkMutations.push({
						from: pos,
						to: pos + node.nodeSize,
						oldMark: mark,
						newMark,
					});
				}
			});

			if (node.text?.includes(originalUrl)) {
				const startIdx = node.text.indexOf(originalUrl);
				textReplaceMutations.push({
					from: pos + startIdx,
					to: pos + startIdx + originalUrl.length,
					newUrl,
				});
				return false;
			}
		}

		return true;
	});

	// Apply card node mutations in reverse order (setNodeMarkup doesn't change positions)
	for (let i = cardMutations.length - 1; i >= 0; i--) {
		const { pos, newAttrs } = cardMutations[i];
		tr.setNodeMarkup(pos, undefined, newAttrs);
	}

	// Apply extension node mutations in reverse order
	for (let i = extensionMutations.length - 1; i >= 0; i--) {
		const { pos, newAttrs } = extensionMutations[i];
		tr.setNodeMarkup(pos, undefined, newAttrs);
	}

	// Apply link mark mutations in reverse order (removeMark/addMark don't change positions)
	for (let i = linkMarkMutations.length - 1; i >= 0; i--) {
		const { from, to, oldMark, newMark } = linkMarkMutations[i];
		tr.removeMark(from, to, oldMark);
		tr.addMark(from, to, newMark);
	}

	// Apply text replacements in reverse order (replaceWith can change text length)
	for (let i = textReplaceMutations.length - 1; i >= 0; i--) {
		const { from, to, newUrl: url } = textReplaceMutations[i];
		tr.replaceWith(from, to, tr.doc.type.schema.text(url));
	}
}

type NodeDeletion = {
	from: number;
	to: number;
};

type LinkMarkRemoval = {
	from: number;
	mark: PMNode['marks'][number];
	to: number;
};

type TextDeletion = {
	from: number;
	to: number;
};

/**
 * Searches the editor document for any occurrence of the given URL and
 * removes it. Mirrors the coverage of `updateUrlInDoc`:
 *  - inlineCard / blockCard / embedCard nodes whose `url` includes the URL
 *  - `extension` nodes whose `parameters.url` or
 *    `parameters.macroParams.url.value` includes the URL
 *  - `link` marks whose `href` includes the URL (mark stripped from the range)
 *  - Plain text containing the URL (the matched substring is deleted)
 *
 * All occurrences in the doc are removed. This is intentional — when the
 * user does not have permission to re-parent a Rovo system-space whiteboard,
 * leaving any reference to the URL on the page would orphan it. Per the
 * DACI Option 2A decision we accept removing duplicate occurrences
 * elsewhere in the doc to guarantee the orphaned link is gone.
 *
 * Mutations are collected during traversal and applied in REVERSE document
 * order so earlier positions are not invalidated by later mutations. All
 * mutations land in a SINGLE transaction so undo restores the diagram in
 * one step and collab streams a single coherent change.
 *
 * Returns the number of occurrences removed (sum of all mutation buckets).
 * A return of 0 means the URL was no longer in the doc (e.g. a remote
 * collaborator already removed it, or the user undid the paste) — the
 * caller can treat this as a safe no-op.
 */
function removeUrlFromDoc(tr: Transaction, url: string): number {
	const nodeDeletions: NodeDeletion[] = [];
	const linkMarkRemovals: LinkMarkRemoval[] = [];
	const textDeletions: TextDeletion[] = [];

	tr.doc.descendants((node, pos) => {
		if (CARD_NODE_NAMES.has(node.type.name) && node.attrs?.url?.includes(url)) {
			nodeDeletions.push({ from: pos, to: pos + node.nodeSize });
			return false;
		}

		if (node.type.name === 'extension') {
			const flatUrl: string | undefined = node.attrs?.parameters?.url;
			const macroUrl: string | undefined = node.attrs?.parameters?.macroParams?.url?.value;
			if (
				(typeof flatUrl === 'string' && flatUrl.includes(url)) ||
				(typeof macroUrl === 'string' && macroUrl.includes(url))
			) {
				nodeDeletions.push({ from: pos, to: pos + node.nodeSize });
				return false;
			}
		}

		if (node.isText) {
			node.marks?.forEach((mark) => {
				if (mark.type.name === 'link' && mark.attrs?.href?.includes(url)) {
					linkMarkRemovals.push({
						from: pos,
						to: pos + node.nodeSize,
						mark,
					});
				}
			});

			if (node.text?.includes(url)) {
				const startIdx = node.text.indexOf(url);
				textDeletions.push({
					from: pos + startIdx,
					to: pos + startIdx + url.length,
				});
				// Keep traversing — a text node could contain multiple
				// occurrences but indexOf only finds the first; if needed
				// we'd loop here. For now matching updateUrlInDoc semantics.
				return false;
			}
		}

		return true;
	});

	// Apply mark removals FIRST. removeMark doesn't change any positions,
	// so doing this before any deletions means we don't need to map the
	// captured mark positions through `tr.mapping`.
	for (let i = linkMarkRemovals.length - 1; i >= 0; i--) {
		const { from, to, mark } = linkMarkRemovals[i];
		if (from < to) {
			tr.removeMark(from, to, mark);
		}
	}

	// Merge node and text deletions into a single bucket sorted by `from`,
	// then apply in REVERSE document order. Doing them in one pass —
	// rather than node-deletions-first then text-deletions — means each
	// deletion only affects positions strictly later in the doc, and our
	// reverse traversal already accounts for that. No `tr.mapping` calls
	// needed.
	const allDeletions: Array<NodeDeletion | TextDeletion> = [
		...nodeDeletions,
		...textDeletions,
	].sort((a, b) => a.from - b.from);
	for (let i = allDeletions.length - 1; i >= 0; i--) {
		const { from, to } = allDeletions[i];
		if (from < to) {
			tr.delete(from, to);
		}
	}

	return nodeDeletions.length + linkMarkRemovals.length + textDeletions.length;
}

/**
 * Extracts a MAUI app URL from a pasted ProseMirror Slice by inspecting
 * extension nodes for a URL in the macroParams.url.value or parameters.url
 * structure. Returns the URL string if found, or null.
 */
function extractMauiAppUrlFromSlice(slice?: Slice): string | null {
	if (!slice?.content?.descendants) {
		return null;
	}

	let foundUrl: string | null = null;

	slice.content.descendants((node: PMNode) => {
		if (foundUrl) {
			return false;
		}

		if (node.type.name === 'extension') {
			const macroUrl = node.attrs?.parameters?.macroParams?.url?.value;
			if (macroUrl && typeof macroUrl === 'string') {
				foundUrl = macroUrl;
				return false;
			}
			const flatUrl = node.attrs?.parameters?.url;
			if (flatUrl && typeof flatUrl === 'string') {
				foundUrl = flatUrl;
				return false;
			}
		}

		return true;
	});

	return foundUrl;
}

/**
 * Kicks off the async move workflow:
 * 1. Resolves the whiteboard's actual space key
 * 2. If the space starts with ~ROVO (Rovo system space), proceeds with the move
 * 3. Otherwise, skips the move (whiteboard is already in a real space)
 *
 * On success, updates the URL in the editor document to replace the space
 * key with the target space key.
 */
function startMoveWorkflow({
	view,
	config,
	linkInfo,
	currentPageId,
}: {
	config: MauiDiagramPasteConfig;
	currentPageId: string;
	linkInfo: MauiDiagramLinkInfo;
	view: EditorView;
}): void {
	const experienceTracker = ExperienceTrackerHandler.getInstance().experienceTracker;
	const experienceAttributes: Record<string, unknown> = {
		contentId: linkInfo.contentId,
		contentType: config.getCurrentContentType?.(),
		remixType: 'diagram',
		source: REMIX_PASTE_SOURCE,
		targetPageId: currentPageId,
		conversationChannelId: linkInfo.conversationContext?.conversationId,
	};

	if (!config.resolveContentSpaceKey || !config.finalizeAfterMove || !config.targetSpaceKey) {
		return;
	}

	const { resolveContentSpaceKey, finalizeAfterMove, targetSpaceKey } = config;
	let finalizeStarted = false;
	startRemixPasteExperience(experienceAttributes);

	resolveContentSpaceKey(linkInfo.contentId)
		.then((spaceKey) => {
			if (!spaceKey || !spaceKey.startsWith(ROVO_SPACE_PREFIX)) {
				// Not in a Rovo system space — already moved or not a MAUI diagram.
				// Update the URL in the doc if the space was resolved (so re-paste
				// from the same clipboard doesn't trigger another check).
				if (spaceKey) {
					const movedUrl = buildMovedUrl(linkInfo.originalUrl, spaceKey);
					const updateTr = view.state.tr;
					updateUrlInDoc(updateTr, linkInfo.originalUrl, movedUrl);
					view.dispatch(updateTr);
				}
				succeedRemixPasteExperience({
					...experienceAttributes,
					finalizeNeeded: false,
					resolvedSpaceKey: spaceKey,
				});
				return;
			}

			finalizeStarted = true;
			experienceTracker.start({
				id: ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE,
				name: ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE,
				attributes: { ...experienceAttributes, remixCreateId: getActiveRemixCreateId() },
			});

			const startTr = view.state.tr;
			startTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.MOVE_STARTED,
			});
			view.dispatch(startTr);

			// The backend handles BOTH the move (cross-space reparent) and the
			// finalize. We never do a frontend GraphQL move — it would be
			// redundant work and double-trigger any side effects.
			return finalizeAfterMove(linkInfo.contentId, currentPageId, linkInfo.conversationContext)
				.then(() => {
					const movedUrl = buildMovedUrl(linkInfo.originalUrl, targetSpaceKey);
					const successTr = view.state.tr;

					updateUrlInDoc(successTr, linkInfo.originalUrl, movedUrl);

					successTr.setMeta(mauiDiagramPastePluginKey, {
						type: ActionType.MOVE_SUCCEEDED,
					});
					view.dispatch(successTr);

					config.fireAnalyticsEvent?.({
						action: 'moved',
						actionSubject: 'mauiDiagram',
						actionSubjectId: 'move',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							contentId: linkInfo.contentId,
							targetPageId: currentPageId,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE,
						attributes: experienceAttributes,
					});
					succeedRemixPasteExperience(experienceAttributes);
				})
				.catch((error: Error) => {
					if (config.isFinalizePermissionError?.(error)) {
						// The user lacks space-create permission and was informed via an
						// error flag (dispatched by the outer .catch). The UX flow went as
						// expected, so succeed the experience with a denoting attribute.
						experienceTracker.succeed({
							name: ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE,
							attributes: {
								...experienceAttributes,
								isPermissionError: true,
							},
						});
						failRemixPasteExperience(error, {
							...experienceAttributes,
							isPermissionError: true,
						});
					} else {
						experienceTracker.fail({
							teamName: 'CC CE MAU',
							name: ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE,
							error,
							attributes: experienceAttributes,
						});
						failRemixPasteExperience(error, experienceAttributes);
					}
					// Re-throw so the outer .catch still handles UI cleanup.
					throw error;
				});
		})
		.catch((error: Error) => {
			if (!finalizeStarted) {
				failRemixPasteExperience(error, {
					...experienceAttributes,
					failedDueTo: 'resolveContentSpaceKey',
				});
			}
			const failTr = view.state.tr;

			// When the removal feature is on, also strip the pasted reference
			// from the doc so the user isn't left with an orphaned link in a
			// temporary Rovo space. This is the original DACI Option 2A
			// intent — applied here for finalize failures (covers BOTH the
			// monolith path and the convo-ai path, since `finalizeAfterMove`
			// is the single funnel for both).
			//
			// Gated behind the same kill switch as the pre-flight permission
			// removal so the two paths share one rollback. When the kill
			// switch is ON the behaviour reverts to flag only, no removal.
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- Pre-existing kill switch shipped via Statsig experiment-as-flag (the team owning this gate uses experiments for fast rollback). Migration to a boolean feature gate is tracked separately and is not part of MAUI-556.
			const killSwitchOn = FeatureGates.getExperimentValue<boolean>(
				'confluence_maui_remove_diagram_on_paste_fail',
				'value',
				false,
			);
			let occurrencesRemoved = 0;
			if (!killSwitchOn) {
				occurrencesRemoved = removeUrlFromDoc(failTr, linkInfo.originalUrl);
			}

			config.fireAnalyticsEvent?.({
				action: ACTION.ERROR,
				actionSubject: 'mauiDiagram',
				actionSubjectId: 'move',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					contentId: linkInfo.contentId,
					targetPageId: currentPageId,
					errorMessage: error?.message,
					errorStack: error?.stack,
					occurrencesRemoved,
				},
			});

			failTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.MOVE_FAILED,
				useLegacyCopy: killSwitchOn,
			});
			// addToHistory defaults to true; setting explicitly so undo
			// restores the diagram in a single step alongside the flag.
			failTr.setMeta('addToHistory', true);
			view.dispatch(failTr);
		});
}

/**
 * Async duplicate workflow for MAUI app links: duplicates the app, then rewrites matching URLs in the doc.
 * Permission errors surface the BE's human-readable message verbatim; other failures use fallback copy.
 */
function startDuplicateWorkflow({
	view,
	config,
	linkInfo,
	currentPageId,
	getIntl,
	pastedApp,
}: {
	config: MauiDiagramPasteConfig;
	currentPageId: string;
	getIntl: PMPluginFactoryParams['getIntl'];
	linkInfo: MauiAppLinkInfo;
	pastedApp?: PastedMauiApp;
	view: EditorView;
}): void {
	const duplicateMauiApp = config.duplicateMauiApp;
	if (!duplicateMauiApp || (pastedApp && view.isDestroyed)) {
		return;
	}

	const currentContentType = config.getCurrentContentType?.();
	const experienceTracker = getMauiExperienceTracker();
	const experienceAttributes = {
		appId: linkInfo.appId,
		contentType: currentContentType,
		currentPageId,
		remixType: 'app',
		source: REMIX_PASTE_SOURCE,
	};

	startRemixPasteExperience(experienceAttributes);
	experienceTracker.start({
		name: ExperienceName.REMIX_DUPLICATE_APP,
		id: ExperienceName.REMIX_DUPLICATE_APP,
		attributes: experienceAttributes,
	});

	const startTr = view.state.tr;
	startTr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.DUPLICATE_STARTED,
	});
	view.dispatch(startTr);

	Promise.resolve()
		.then(() => duplicateMauiApp(linkInfo.appId, currentPageId, currentContentType))
		.then(({ id: newAppId, attempts, statusCode }) => {
			if (pastedApp && view.isDestroyed) {
				return;
			}
			const wasNoOp = newAppId === linkInfo.appId;
			try {
				const successTr = view.state.tr;

				if (!wasNoOp) {
					if (pastedApp) {
						updatePastedMauiApp(successTr, pastedApp, newAppId);
					} else {
						const duplicatedUrl = buildDuplicatedMauiAppUrl(linkInfo.originalUrl, newAppId);
						updateUrlInDoc(successTr, linkInfo.originalUrl, duplicatedUrl);
					}
				}

				successTr.setMeta(mauiDiagramPastePluginKey, {
					type: ActionType.DUPLICATE_SUCCEEDED,
				});
				view.dispatch(successTr);
			} catch (renderError) {
				const error =
					renderError instanceof Error ? renderError : new Error(DUPLICATE_FALLBACK_ERROR_MESSAGE);
				const failTr = view.state.tr;
				failTr.setMeta(mauiDiagramPastePluginKey, {
					type: ActionType.DUPLICATE_FAILED,
					errorMessage:
						renderError instanceof Error
							? error.message
							: formatFlagMessage(getIntl, messages.mauiAppDuplicateErrorDescription),
					// `/duplicate` already succeeded; this catch only handles editor render/update errors.
					isPermissionError: false,
				});
				view.dispatch(failTr);

				config.fireAnalyticsEvent?.({
					eventType: EVENT_TYPE.OPERATIONAL,
					actionSubject: 'mauiAppDuplicate',
					actionSubjectId: 'mauiPaste',
					action: 'failed',
					attributes: {
						originalId: linkInfo.appId,
						newId: newAppId,
						attempts,
						statusCode,
						wasNoOp,
						errorMessage: error.message,
						isPermissionError: false,
						renderError: true,
					},
				});

				failRemixDuplicateAppAndInsert(error, {
					...experienceAttributes,
					newAppId,
					attempts,
					statusCode,
					wasNoOp,
				});
				failRemixPasteExperience(error, {
					...experienceAttributes,
					newAppId,
					attempts,
					statusCode,
					wasNoOp,
				});
				return;
			}

			config.fireAnalyticsEvent?.({
				eventType: EVENT_TYPE.OPERATIONAL,
				actionSubject: 'mauiAppDuplicate',
				actionSubjectId: 'mauiPaste',
				action: 'succeeded',
				attributes: {
					originalId: linkInfo.appId,
					newId: newAppId,
					attempts,
					statusCode,
					wasNoOp,
				},
			});

			experienceTracker.succeed({
				name: ExperienceName.REMIX_DUPLICATE_APP,
				attributes: {
					...experienceAttributes,
					newAppId,
					attempts,
					statusCode,
					wasNoOp,
				},
			});
			succeedRemixPasteExperience({
				...experienceAttributes,
				newAppId,
				attempts,
				statusCode,
				wasNoOp,
			});
		})
		.catch((error: Error) => {
			if (pastedApp && view.isDestroyed) {
				return;
			}
			const fallbackErrorMessage = formatFlagMessage(
				getIntl,
				messages.mauiAppDuplicateErrorDescription,
			);
			const statusCode = (error as { statusCode?: number } | null)?.statusCode;
			const errorCode = (error as { code?: string } | null)?.code;
			const attempts = (error as { attempts?: 1 | 2 } | null)?.attempts;
			const isPermissionError = statusCode === 403;
			const errorForExperience =
				error instanceof Error ? error : new Error(DUPLICATE_FALLBACK_ERROR_MESSAGE);
			const failTr = view.state.tr;
			failTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.DUPLICATE_FAILED,
				errorMessage: error?.message || fallbackErrorMessage,
				isPermissionError,
			});
			view.dispatch(failTr);

			config.fireAnalyticsEvent?.({
				eventType: EVENT_TYPE.OPERATIONAL,
				actionSubject: 'mauiAppDuplicate',
				actionSubjectId: 'mauiPaste',
				action: 'failed',
				attributes: {
					originalId: linkInfo.appId,
					errorMessage: error?.message,
					isPermissionError,
					statusCode,
					errorCode,
					attempts,
				},
			});

			failRemixDuplicateAppAndInsert(errorForExperience, {
				...experienceAttributes,
				statusCode,
				errorCode,
				isPermissionError,
				attempts,
			});
			failRemixPasteExperience(errorForExperience, {
				...experienceAttributes,
				statusCode,
				errorCode,
				isPermissionError,
				attempts,
			});
		});
}

/**
 * Public arguments accepted by `runEmbedFinalizeWorkflow`.
 *
 * `originalUrl` is the exact URL stored on the embed extension node — it is
 * the search key used by `updateUrlInDoc` to rewrite all occurrences in the
 * document on success. The whiteboard `contentId` is parsed from this URL by
 * the caller (the indicator) so it can also dispatch `CHECKING` synchronously
 * before the workflow is invoked.
 */
/**
 * Mode controlling how `runEmbedFinalizeWorkflow` behaves once it has
 * detected that the embed lives in a Rovo system space:
 *
 *   - `'probe'` — only determine whether finalisation is needed. After
 *     `resolveContentSpaceKey` confirms a `~ROVO…` prefix, the workflow
 *     dispatches `NEEDS_FINALIZE` and returns without calling
 *     `finalizeAfterMove`. Used by the in-editor banner to bootstrap its
 *     status without silently saving the page on render.
 *   - `'finalize'` (default) — perform the full save. After detecting a
 *     `~ROVO…` prefix the workflow dispatches `FINALIZING` and calls
 *     `config.finalizeAfterMove(...)` to move the diagram to the current
 *     page, then dispatches `FINALIZED` (or `FAILED` on error).
 */
export type MauiEmbedFinalizeMode = 'probe' | 'finalize';

export type MauiEmbedFinalizeWorkflowArgs = {
	contentId: string;
	conversationContext?: RovoConversationContext;
	mode?: MauiEmbedFinalizeMode;
	originalUrl: string;
	/** When supplied, skips the probe and finalizes into the picked space. */
	targetSpace?: MauiEmbedTargetSpace;
};

function runEmbedFinalizeWorkflow({
	view,
	config,
	args,
	currentPageId,
	getIntl,
}: {
	args: MauiEmbedFinalizeWorkflowArgs;
	config: MauiDiagramPasteConfig;
	currentPageId: string;
	getIntl: PMPluginFactoryParams['getIntl'];
	view: EditorView;
}): Promise<void> {
	const { contentId, originalUrl, conversationContext, mode = 'finalize', targetSpace } = args;

	const dispatchStatus = (
		status: MauiEmbedFinalizeStatus,
		extra?: { errorMessage?: string; flag?: MauiDiagramPasteFlag | null },
	) => {
		const tr = view.state.tr;
		tr.setMeta(mauiDiagramPastePluginKey, {
			type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
			contentId,
			originalUrl,
			status,
			errorMessage: extra?.errorMessage,
			flag: extra?.flag,
		});
		view.dispatch(tr);
	};

	if (!config.resolveContentSpaceKey || !config.finalizeAfterMove || !config.targetSpaceKey) {
		dispatchStatus(MauiEmbedFinalizeStatus.NOT_NEEDED);
		return Promise.resolve();
	}

	const { resolveContentSpaceKey, finalizeAfterMove, targetSpaceKey } = config;

	dispatchStatus(MauiEmbedFinalizeStatus.CHECKING);

	return resolveContentSpaceKey(contentId)
		.then(async (spaceKey) => {
			if (!spaceKey || !spaceKey.startsWith(ROVO_SPACE_PREFIX)) {
				if (spaceKey && mode !== 'probe') {
					const movedUrl = buildMovedUrl(originalUrl, spaceKey);
					if (movedUrl !== originalUrl) {
						const updateTr = view.state.tr;
						updateUrlInDoc(updateTr, originalUrl, movedUrl);
						view.dispatch(updateTr);
					}
				}
				dispatchStatus(MauiEmbedFinalizeStatus.NOT_NEEDED);
				return;
			}

			// Probe mode: surface that finalisation is needed but do NOT
			// kick off the actual save. The user-facing banner triggers
			// `mode: 'finalize'` separately when the action button is
			// clicked.
			if (mode === 'probe') {
				dispatchStatus(MauiEmbedFinalizeStatus.NEEDS_FINALIZE);
				return;
			}

			// Skip the probe when the user already picked a destination
			// from the picker (which only offers spaces they can write to).
			const siteId = config.finalizeLocation?.siteId;
			if (
				!targetSpace &&
				siteId &&
				// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
				!FeatureGates.getExperimentValue<boolean>(
					'maui-native-embed-finalize-diagram-anywhere',
					'value',
					false,
				)
			) {
				const allowed = await canCreatePageInSpace(siteId, targetSpaceKey);
				if (!allowed) {
					dispatchStatus(MauiEmbedFinalizeStatus.NEEDS_SPACE_SELECTION, {
						flag: {
							id: `maui-embed-finalize-space-picker-${contentId}`,
							type: MauiDiagramPasteFlagType.INFO,
							title: formatFlagMessage(getIntl, messages.flagFinalizeSpacePickerTitle),
							description: formatFlagMessage(getIntl, messages.flagFinalizeSpacePickerDescription),
						} satisfies MauiDiagramPasteFlag,
					});
					executeOpenMauiEmbedSpacePicker(view, { contentId });
					return;
				}
			}

			dispatchStatus(MauiEmbedFinalizeStatus.FINALIZING);

			// When the user picked a different target space, the parent
			// MUST live in that space — falling back to `currentPageId`
			// would point at a page in the original (Rovo system) space.
			const parentPageId = targetSpace?.parentId ?? currentPageId;

			return finalizeAfterMove(contentId, parentPageId, conversationContext, targetSpace).then(
				() => {
					const movedUrl = buildMovedUrl(originalUrl, targetSpace?.key ?? targetSpaceKey);
					const successTr = view.state.tr;
					updateUrlInDoc(successTr, originalUrl, movedUrl);
					successTr.setMeta(mauiDiagramPastePluginKey, {
						type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
						contentId,
						status: MauiEmbedFinalizeStatus.FINALIZED,
						flag: {
							id: `maui-embed-finalize-success-${contentId}`,
							type: MauiDiagramPasteFlagType.SUCCESS,
							title: formatFlagMessage(getIntl, messages.flagFinalizeSuccessTitle),
							description: targetSpace
								? formatFlagMessage(getIntl, messages.flagFinalizeSuccessDescriptionPickedSpace)
								: formatFlagMessage(getIntl, messages.flagFinalizeSuccessDescriptionCurrentPage),
						} satisfies MauiDiagramPasteFlag,
					});
					view.dispatch(successTr);

					config.fireAnalyticsEvent?.({
						action: 'finalized',
						actionSubject: 'mauiDiagram',
						actionSubjectId: 'embedFinalize',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							contentId,
							targetPageId: currentPageId,
						},
					});
				},
			);
		})
		.catch((error: Error) => {
			config.fireAnalyticsEvent?.({
				action: ACTION.ERROR,
				actionSubject: 'mauiDiagram',
				actionSubjectId: 'embedFinalize',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					contentId,
					targetPageId: currentPageId,
					errorMessage: error?.message,
					errorStack: error?.stack,
				},
			});

			dispatchStatus(MauiEmbedFinalizeStatus.FAILED, {
				errorMessage: error?.message,
				flag: {
					id: `maui-embed-finalize-error-${contentId}`,
					type: MauiDiagramPasteFlagType.ERROR,
					title: formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorTitle),
					description: formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorDescription),
				} satisfies MauiDiagramPasteFlag,
			});
		});
}

function runTargetSpaceEmbedFinalizeWorkflow({
	view,
	config,
	args,
	getIntl,
}: {
	args: MauiEmbedFinalizeWorkflowArgs & { targetSpace: MauiEmbedTargetSpace };
	config: MauiDiagramPasteConfig;
	getIntl: PMPluginFactoryParams['getIntl'];
	view: EditorView;
}): Promise<void> {
	const { contentId, conversationContext, originalUrl, targetSpace } = args;

	const dispatchStatus = (
		status: MauiEmbedFinalizeStatus,
		extra?: { errorMessage?: string; flag?: MauiDiagramPasteFlag | null },
	) => {
		const tr = view.state.tr;
		tr.setMeta(mauiDiagramPastePluginKey, {
			type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
			contentId,
			originalUrl,
			status,
			errorMessage: extra?.errorMessage,
			flag: extra?.flag,
		});
		view.dispatch(tr);
	};

	// No permission probe is needed on the picked-location path (the user has
	// explicitly chosen a writable space), so go straight to FINALIZING below
	// rather than passing through CHECKING.
	if (!config.finalizeAfterMove) {
		const errorMessage = formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorDescription);
		dispatchStatus(MauiEmbedFinalizeStatus.FAILED, {
			errorMessage,
			flag: {
				id: `maui-embed-finalize-error-${contentId}`,
				type: MauiDiagramPasteFlagType.ERROR,
				title: formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorTitle),
				description: errorMessage,
			} satisfies MauiDiagramPasteFlag,
		});
		return Promise.resolve();
	}

	dispatchStatus(MauiEmbedFinalizeStatus.FINALIZING);

	return config
		.finalizeAfterMove(contentId, targetSpace.parentId, conversationContext, targetSpace)
		.then(() => {
			const movedUrl = buildMovedUrl(originalUrl, targetSpace.key);
			const successTr = view.state.tr;
			updateUrlInDoc(successTr, originalUrl, movedUrl);
			successTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
				contentId,
				status: MauiEmbedFinalizeStatus.FINALIZED,
				flag: {
					id: `maui-embed-finalize-success-${contentId}`,
					type: MauiDiagramPasteFlagType.SUCCESS,
					title: formatFlagMessage(getIntl, messages.flagFinalizeSuccessTitle),
					description: formatFlagMessage(
						getIntl,
						messages.flagFinalizeSuccessDescriptionPickedSpace,
					),
				} satisfies MauiDiagramPasteFlag,
			});
			view.dispatch(successTr);

			config.fireAnalyticsEvent?.({
				action: 'finalized',
				actionSubject: 'mauiDiagram',
				actionSubjectId: 'embedFinalize',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					contentId,
					targetPageId: targetSpace.parentId,
					targetSpaceId: targetSpace.id,
				},
			});
		})
		.catch((error: Error) => {
			config.fireAnalyticsEvent?.({
				action: ACTION.ERROR,
				actionSubject: 'mauiDiagram',
				actionSubjectId: 'embedFinalize',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					contentId,
					targetPageId: targetSpace.parentId,
					targetSpaceId: targetSpace.id,
					errorMessage: error?.message,
					errorStack: error?.stack,
				},
			});

			dispatchStatus(MauiEmbedFinalizeStatus.FAILED, {
				errorMessage: error?.message,
				flag: {
					id: `maui-embed-finalize-error-${contentId}`,
					type: MauiDiagramPasteFlagType.ERROR,
					title: formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorTitle),
					description: formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorDescription),
				} satisfies MauiDiagramPasteFlag,
			});
		});
}

/**
 * Imperative entry point used by the malleableUi plugin's
 * `finalizeMauiEmbedFromIndicator` action. Returns a Promise that resolves
 * (with `void`) once the workflow has settled. Errors are surfaced via the
 * plugin state (and the error AK Flag); the returned Promise itself never
 * rejects.
 *
 * The picked-location path uses `targetSpace.parentId`; the current-page path
 * reads `currentPageId` from the same getter used by the paste workflow.
 */
export function executeMauiEmbedFinalize(
	view: EditorView,
	config: MauiDiagramPasteConfig,
	args: MauiEmbedFinalizeWorkflowArgs,
): Promise<void> {
	if (
		args.targetSpace &&
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
		FeatureGates.getExperimentValue<boolean>(
			'maui-native-embed-finalize-diagram-anywhere',
			'value',
			false,
		)
	) {
		return Promise.resolve();
	}

	const getIntl = knownViewIntlGetters.get(view) ?? fallbackGetIntl;

	if (args.targetSpace) {
		return runTargetSpaceEmbedFinalizeWorkflow({
			view,
			config,
			args: { ...args, targetSpace: args.targetSpace },
			getIntl,
		});
	}

	const currentPageId = config.getCurrentPageId?.();
	if (!currentPageId) {
		const errorMessage = formatFlagMessage(
			getIntl,
			messages.mauiDiagramFinalizeMissingPageDescription,
		);
		const tr = view.state.tr;
		tr.setMeta(mauiDiagramPastePluginKey, {
			type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
			contentId: args.contentId,
			status: MauiEmbedFinalizeStatus.FAILED,
			errorMessage,
			flag: {
				id: `maui-embed-finalize-error-${args.contentId}`,
				type: MauiDiagramPasteFlagType.ERROR,
				title: formatFlagMessage(getIntl, messages.mauiDiagramFinalizeErrorTitle),
				description: errorMessage,
			} satisfies MauiDiagramPasteFlag,
		});
		view.dispatch(tr);
		return Promise.resolve();
	}
	return runEmbedFinalizeWorkflow({ view, config, args, currentPageId, getIntl });
}

/**
 * Imperatively sets the finalize status for a whiteboard embed (by content id),
 * driving `MauiNativeEmbedBanner` visibility. Used by the Rovo remix finalizer.
 */
export function executeSetMauiEmbedFinalizeStatus(
	view: EditorView,
	contentId: string,
	status: MauiEmbedFinalizeStatus,
): void {
	if (!contentId) {
		return;
	}
	const tr = view.state.tr;
	tr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
		contentId,
		status,
	});
	view.dispatch(tr);
}

/** Resets the finalize state for one embed so the next mount re-probes. */
export function executeCancelMauiEmbedSpaceSelection(
	view: EditorView,
	args: { contentId: string },
): void {
	if (
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
		FeatureGates.getExperimentValue<boolean>(
			'maui-native-embed-finalize-diagram-anywhere',
			'value',
			false,
		)
	) {
		return;
	}

	const tr = view.state.tr;
	tr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.EMBED_FINALIZE_RESET,
		contentId: args.contentId,
	});
	view.dispatch(tr);
}

/** Opens the space picker for a NEEDS_SPACE_SELECTION embed. */
export function executeOpenMauiEmbedSpacePicker(
	view: EditorView,
	args: { contentId: string },
): void {
	if (
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
		FeatureGates.getExperimentValue<boolean>(
			'maui-native-embed-finalize-diagram-anywhere',
			'value',
			false,
		)
	) {
		return;
	}

	const tr = view.state.tr;
	tr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.EMBED_FINALIZE_OPEN_PICKER,
		contentId: args.contentId,
	});
	view.dispatch(tr);
}

/** Closes the space picker without resetting the embed's NEEDS_SPACE_SELECTION status. */
export function executeClosePickerMauiEmbedSpaceSelection(
	view: EditorView,
	args: { contentId: string },
): void {
	if (
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
		FeatureGates.getExperimentValue<boolean>(
			'maui-native-embed-finalize-diagram-anywhere',
			'value',
			false,
		)
	) {
		return;
	}

	const tr = view.state.tr;
	tr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.EMBED_FINALIZE_CLOSE_PICKER,
		contentId: args.contentId,
	});
	view.dispatch(tr);
}

/**
 * Creates the ProseMirror plugin that handles paste detection and movement
 * of MAUI diagram whiteboards to the current page.
 *
 * The paste handler detects the MAUI link and captures it in a closure-scoped
 * queue (not in plugin state) so that no transactions are dispatched during
 * the card plugin's async URL → embedCard resolution window. The move
 * workflow is kicked off after a delay, only dispatching transactions
 * (MOVE_STARTED, MOVE_SUCCEEDED, MOVE_FAILED) once the card plugin has
 * finished resolving.
 */
export function createMauiDiagramPastePmPlugin(
	dispatch: Dispatch,
	config: MauiDiagramPasteConfig,
	getIntl: PMPluginFactoryParams['getIntl'] = fallbackGetIntl,
): SafePlugin {
	// Pending moves are tracked outside plugin state to avoid dispatching
	// transactions that would interfere with the card plugin's async
	// URL-to-embedCard resolution (which checks document structure via
	// isFreshlyPastedOnNewLine).
	let pendingMoveTimer: ReturnType<typeof setTimeout> | null = null;
	const pendingAppTimers = new Set<ReturnType<typeof setTimeout>>();
	const droppingViews = new WeakSet<EditorView>();

	const cancelPendingMove = () => {
		if (pendingMoveTimer !== null) {
			clearTimeout(pendingMoveTimer);
			pendingMoveTimer = null;
		}
	};

	return new SafePlugin({
		key: mauiDiagramPastePluginKey,

		state: {
			init: (): MauiDiagramPastePluginState => initialState,

			apply(
				tr: ReadonlyTransaction,
				pluginState: MauiDiagramPastePluginState,
			): MauiDiagramPastePluginState {
				const meta = tr.getMeta(mauiDiagramPastePluginKey);
				if (!meta) {
					return pluginState;
				}

				let nextState: MauiDiagramPastePluginState;

				switch (meta.type) {
					case ActionType.MOVE_STARTED:
					case ActionType.DUPLICATE_STARTED:
						nextState = {
							...pluginState,
							isMoving: true,
							activeFlag: null,
						};
						break;
					case ActionType.MOVE_SUCCEEDED:
					case ActionType.DUPLICATE_SUCCEEDED:
						nextState = {
							...pluginState,
							isMoving: false,
							activeFlag: null,
						};
						break;
					case ActionType.MOVE_FAILED:
						// NOTE: copy is intentionally a generic placeholder —
						// the real BE error messages are not user-friendly and
						// are still captured in the analytics event for ops.
						// When the kill switch is ON we surface the pre-PR move-error
						// copy so rollback is faithful (no removal AND no new copy).
						nextState = {
							...pluginState,
							isMoving: false,
							activeFlag: meta.useLegacyCopy
								? {
										id: 'maui-diagram-move-error',
										type: MauiDiagramPasteFlagType.ERROR,
										title: formatFlagMessage(getIntl, messages.mauiDiagramMoveErrorTitle),
										description: formatFlagMessage(
											getIntl,
											messages.mauiDiagramMoveErrorDescription,
										),
									}
								: {
										id: 'maui-diagram-paste-error',
										type: MauiDiagramPasteFlagType.ERROR,
										title: formatFlagMessage(getIntl, messages.mauiDiagramPasteErrorTitle),
										description: formatFlagMessage(
											getIntl,
											messages.mauiDiagramPasteErrorDescription,
										),
									},
						};
						break;
					case ActionType.DUPLICATE_FAILED:
						nextState = {
							...pluginState,
							isMoving: false,
							activeFlag: {
								id: 'maui-app-duplicate-error',
								type: MauiDiagramPasteFlagType.ERROR,
								title: meta.isPermissionError
									? formatFlagMessage(getIntl, messages.mauiAppDuplicatePermissionErrorTitle)
									: formatFlagMessage(getIntl, messages.mauiAppDuplicateErrorTitle),
								description:
									meta.errorMessage ||
									formatFlagMessage(getIntl, messages.mauiAppDuplicateErrorDescription),
							},
						};
						break;
					case ActionType.DISMISS_FLAG:
						nextState = { ...pluginState, activeFlag: null };
						break;
					case ActionType.EMBED_FINALIZE_RESET: {
						const contentId = typeof meta.contentId === 'string' ? meta.contentId : null;
						if (!contentId || !(contentId in pluginState.embedFinalizeStates)) {
							return pluginState;
						}
						const { [contentId]: _removed, ...rest } = pluginState.embedFinalizeStates;
						const activeFlagId = pluginState.activeFlag?.id;
						const isStaleFlagForEntry =
							activeFlagId === `maui-embed-finalize-error-${contentId}` ||
							activeFlagId === `maui-embed-finalize-success-${contentId}`;
						nextState = {
							...pluginState,
							embedFinalizeStates: rest,
							activeFlag: isStaleFlagForEntry ? null : pluginState.activeFlag,
						};
						break;
					}
					case ActionType.EMBED_FINALIZE_OPEN_PICKER: {
						const contentId = typeof meta.contentId === 'string' ? meta.contentId : null;
						const entry = contentId ? pluginState.embedFinalizeStates[contentId] : null;
						if (
							!contentId ||
							!entry ||
							entry.status !== MauiEmbedFinalizeStatus.NEEDS_SPACE_SELECTION
						) {
							return pluginState;
						}
						nextState = {
							...pluginState,
							embedFinalizeStates: {
								...pluginState.embedFinalizeStates,
								[contentId]: { ...entry, pickerOpen: true },
							},
						};
						break;
					}
					case ActionType.EMBED_FINALIZE_CLOSE_PICKER: {
						const contentId = typeof meta.contentId === 'string' ? meta.contentId : null;
						const entry = contentId ? pluginState.embedFinalizeStates[contentId] : null;
						if (!contentId || !entry || !entry.pickerOpen) {
							return pluginState;
						}
						nextState = {
							...pluginState,
							embedFinalizeStates: {
								...pluginState.embedFinalizeStates,
								[contentId]: { ...entry, pickerOpen: false },
							},
						};
						break;
					}
					case ActionType.EMBED_FINALIZE_STATUS_CHANGED: {
						const contentId = typeof meta.contentId === 'string' ? meta.contentId : null;
						if (!contentId) {
							return pluginState;
						}
						const nextEntry: MauiEmbedFinalizeStateEntry = {
							status: meta.status,
							errorMessage: meta.errorMessage,
							originalUrl: meta.originalUrl,
						};
						const existingEntry = pluginState.embedFinalizeStates[contentId];
						// Reference-equality short-circuit so unrelated transactions don't
						// trigger downstream re-renders for indicators not affected by
						// this change.
						if (
							existingEntry &&
							existingEntry.status === nextEntry.status &&
							existingEntry.errorMessage === nextEntry.errorMessage &&
							existingEntry.originalUrl === nextEntry.originalUrl
						) {
							return pluginState;
						}
						nextState = {
							...pluginState,
							embedFinalizeStates: {
								...pluginState.embedFinalizeStates,
								[contentId]: nextEntry,
							},
							activeFlag: meta.flag === undefined ? pluginState.activeFlag : (meta.flag ?? null),
						};
						break;
					}
					default:
						return pluginState;
				}

				dispatch(mauiDiagramPastePluginKey, nextState);
				return nextState;
			},
		},

		view(view: EditorView) {
			// Track this view for the duration of its lifetime so async,
			// view-spanning workflows (e.g. the in-embed finalize action
			// triggered from the malleableUi plugin's `actions` surface)
			// can resolve the correct EditorView to dispatch against —
			// even when multiple editors are concurrently alive.
			knownViews.add(view);
			knownViewIntlGetters.set(view, getIntl);
			return {
				update(updatedView: EditorView, prevState: EditorState) {
					// Unlike `state.apply`, the owning view is in scope here for multi-editor pages.
					if (updatedView.state.doc !== prevState.doc) {
						scheduleRemovedMauiEmbedNotification(updatedView);
					}
				},
				destroy() {
					cancelPendingMove();
					pendingAppTimers.forEach(clearTimeout);
					pendingAppTimers.clear();
					notifyAllMauiEmbedsRemoved(view);
					knownViews.delete(view);
					knownViewIntlGetters.delete(view);
				},
			};
		},

		props: {
			handleDOMEvents: {
				drop(view) {
					// transformPasted also runs for drops, including drops from another editor
					// where view.dragging is null. Keep this clipboard fix out of both paths.
					droppingViews.add(view);
					queueMicrotask(() => droppingViews.delete(view));
					return false;
				},
			},
			transformPasted(slice: Slice, view: EditorView) {
				if (droppingViews.has(view) || view.dragging || !sliceContainsMauiApp(slice)) {
					return slice;
				}
				return isExperimentEnabled('cc_maui_editing_experience')
					? prepareMauiAppPaste(slice)
					: slice;
			},
			handlePaste(view: EditorView, event: ClipboardEvent, slice?: Slice) {
				const { clipboardData } = event;
				if (!clipboardData) {
					return false;
				}

				const appsInSlice = getPastedMauiApps(slice);
				const pastedApps =
					appsInSlice.length > 0 && isExperimentEnabled('cc_maui_editing_experience')
						? appsInSlice
						: [];
				const url = extractPastedUrl(clipboardData);

				const diagramLinkInfo = url ? parseMauiDiagramLink(url) : null;
				let appLinkInfo: MauiAppLinkInfo | null =
					pastedApps[0]?.linkInfo ?? (url && !diagramLinkInfo ? parseMauiAppLink(url) : null);

				// If no MAUI link was found in the plain text, check the pasted
				// Slice for extension nodes containing a MAUI app URL (e.g. when
				// a native embed extension is copy-pasted from another page).
				if (!diagramLinkInfo && !appLinkInfo) {
					const sliceUrl = extractMauiAppUrlFromSlice(slice);
					if (sliceUrl) {
						appLinkInfo = parseMauiAppLink(sliceUrl);
					}
				}

				if (!diagramLinkInfo && !appLinkInfo) {
					return false;
				}

				const currentPageId = config.getCurrentPageId?.();
				// For the move workflow, currentPageId is required. For the
				// duplicate-only workflow (e.g. Jira), the container is resolved
				// inside duplicateMauiApp itself so we can skip this guard.
				// The fg gate ensures this relaxed behaviour only activates once the
				// Jira integration is fully launched.
				if (
					!currentPageId &&
					(diagramLinkInfo || !appLinkInfo || !fg('platform_editor_maui_jira_updates'))
				) {
					// Dispatch the error on the next tick so it doesn't
					// interfere with the paste transaction pipeline
					const errorAction = diagramLinkInfo
						? ActionType.MOVE_FAILED
						: ActionType.DUPLICATE_FAILED;
					const errorMessage = formatFlagMessage(
						getIntl,
						messages.mauiDiagramFinalizeMissingPageDescription,
					);
					setTimeout(() => {
						const tr = view.state.tr;
						tr.setMeta(mauiDiagramPastePluginKey, {
							type: errorAction,
							errorMessage,
						});
						view.dispatch(tr);
					}, 0);

					// Start + immediately fail the experience so SLOs pick it up.
					const missingCurrentPageIdError = new Error(
						'Missing current page ID for MAUI paste workflow.',
					);
					if (diagramLinkInfo && config.resolveContentSpaceKey && config.finalizeAfterMove) {
						const experienceAttributes = {
							contentId: diagramLinkInfo.contentId,
							contentType: config.getCurrentContentType?.(),
							currentPageId: undefined,
							remixType: 'diagram',
							source: REMIX_PASTE_SOURCE,
							reason: 'missingCurrentPageId',
							conversationChannelId: diagramLinkInfo.conversationContext?.conversationId,
						};
						startRemixPasteExperience(experienceAttributes);
						failRemixPasteExperience(missingCurrentPageIdError, experienceAttributes);
					}
					if (appLinkInfo && !diagramLinkInfo && config.duplicateMauiApp) {
						const experienceTracker = getMauiExperienceTracker();
						const experienceAttributes = {
							appId: appLinkInfo.appId,
							contentType: config.getCurrentContentType?.(),
							currentPageId: undefined,
							remixType: 'app',
							source: REMIX_PASTE_SOURCE,
							reason: 'missingCurrentPageId',
						};
						startRemixPasteExperience(experienceAttributes);
						experienceTracker.start({
							name: ExperienceName.REMIX_DUPLICATE_APP,
							id: ExperienceName.REMIX_DUPLICATE_APP,
							attributes: experienceAttributes,
						});
						failRemixDuplicateAppAndInsert(missingCurrentPageIdError, experienceAttributes);
						failRemixPasteExperience(missingCurrentPageIdError, experienceAttributes);
					}
					return false;
				}

				if (pastedApps.length > 0 && !diagramLinkInfo) {
					const reuseAppOnPage = expVal(
						'remix_maui_duplicate_stability_improvement',
						'value',
						false,
					);
					for (const pastedApp of pastedApps) {
						if (
							reuseAppOnPage &&
							currentPageId &&
							(docContainsMauiAppVersion(view.state.doc, pastedApp.linkInfo.appId) ||
								docContainsUrl(view.state.doc, pastedApp.linkInfo.originalUrl))
						) {
							continue;
						}
						// Let the normal paste pipeline insert the prepared slice first. Each
						// pasted embed keeps its own target, including back-to-back pastes.
						cancelPendingMove();
						const timer = setTimeout(() => {
							pendingAppTimers.delete(timer);
							startDuplicateWorkflow({
								view,
								config,
								linkInfo: pastedApp.linkInfo,
								currentPageId: currentPageId ?? '',
								getIntl,
								pastedApp,
							});
						}, 0);
						pendingAppTimers.add(timer);
					}
					return false;
				}

				if (
					expVal('remix_maui_duplicate_stability_improvement', 'value', false) &&
					appLinkInfo &&
					currentPageId &&
					docContainsUrl(view.state.doc, appLinkInfo.originalUrl)
				) {
					return false;
				}

				// Clear any previously scheduled move — if the user pastes
				// back-to-back, only the latest paste triggers a move
				cancelPendingMove();

				// Blogs can't parent whiteboard content, so skip move/finalize.
				// Paste inserts the native embed as-is. User can then finalize
				// it to their specified location from native embed UI.
				const isBlog = config.getCurrentContentType?.() === 'blogpost';

				// Schedule the workflow after a delay to avoid dispatching
				// transactions during the card plugin's async URL → embedCard
				// resolution window. The card plugin's isFreshlyPastedOnNewLine
				// check is sensitive to document/selection state changes.
				pendingMoveTimer = setTimeout(() => {
					pendingMoveTimer = null;
					if (diagramLinkInfo) {
						if (isBlog) {
							return;
						}
						startMoveWorkflow({
							view,
							config,
							linkInfo: diagramLinkInfo,
							currentPageId: currentPageId ?? '',
						});
					} else if (appLinkInfo) {
						startDuplicateWorkflow({
							view,
							config,
							linkInfo: appLinkInfo,
							currentPageId: currentPageId ?? '',
							getIntl,
						});
					}
				}, 0);

				// Return false — let the default paste pipeline handle insertion
				// so the URL is auto-converted to a smart link as normal
				return false;
			},
		},
	});
}
